import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { EntityType } from "@mapmycustomers/shared/enum";
import { ActivityPin, EntityPin, MapEntry } from "@mapmycustomers/shared/types/map";
import PlatformBounds from "@mapmycustomers/shared/types/map/PlatformBounds";
import { EntityTypesSupportingMultiPin } from "@mapmycustomers/shared/types/map/types";
import { getPreviewEntityUniqueElementId } from "@mapmycustomers/shared/util/browser";
import {
  convertLatLngBoundsToPlatformBounds,
  convertLongLatToLatLngLiteral,
} from "@mapmycustomers/shared/util/geo/GeoService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import EntityAnnotation, { EntityAnnotationProps } from "@app/component/EntityAnnotation";
import FeaturedMap, { Interactive, ZoomButtons } from "@app/component/map/FeaturedMap";
import MultiPinRecordListDrawer from "@app/component/map/MultiPinRecordListDrawer";
import MultiPinActivityListDrawer from "@app/component/map/MultiPinRecordListDrawer/MultiPinActivityListDrawer";
import MapFilter, { getEntityTypeByMapFilter } from "@app/scene/reports/enums/MapFilter";
import { showEntityView } from "@app/store/entityView/actions";
import useMapEventHandler from "@app/util/hook/map/useMapEventHandler";
import categorizeMapEntries from "@app/util/map/categorizeMapEnties";

import styles from "./ActivityReportMap.module.scss";
import ActivityFeature from "./component/ActivityFeature";
import ClusterFeature from "./component/ClusterFeature";
import EntityFeature from "./component/EntityFeature";
import MultiPinFeature from "./component/MultiPinFeature";

interface Props {
  filter: MapFilter;
  onChangeBounds: (bounds?: PlatformBounds, zoom?: number) => void;
  onShowPreviewPane: typeof showEntityView;
  pins: MapEntry[];
}

const boundsChangeOptions = { maxWait: 500 };

const ActivityReportMap: React.FC<Props> = ({
  filter,
  onChangeBounds,
  onShowPreviewPane,
  pins,
}) => {
  const isActivity = getEntityTypeByMapFilter(filter) === EntityType.ACTIVITY;

  const [map, setMap] = useState<google.maps.Map | undefined>();
  const [performFittingBounds, needToFitBounds, noNeedToFitBounds] = useBoolean(true);

  const [annotation, setAnnotation] = useState<
    Pick<EntityAnnotationProps, "entityId" | "entityType"> | undefined
  >();
  const handlePinClick = useCallback(
    (entity: ActivityPin | EntityPin, element?: HTMLElement) => {
      if (isActivity) {
        onShowPreviewPane({
          elementId: getPreviewEntityUniqueElementId(element),
          entityId: entity.data.id,
          entityType: EntityType.ACTIVITY,
        });
      } else {
        setAnnotation({ entityId: entity.data.id, entityType: (entity as EntityPin).data.entity });
      }
    },
    [isActivity, onShowPreviewPane]
  );
  const handleHideAnnotation = useCallback(() => setAnnotation(undefined), []);

  const handlePinDoubleClick = useCallback(
    (entity: EntityPin) => {
      onShowPreviewPane({ entityId: entity.data.id, entityType: entity.data.entity });
    },
    [onShowPreviewPane]
  );
  const fitBounds = useCallback(() => {
    if (map && pins.length > 0 && performFittingBounds) {
      const bounds = new google.maps.LatLngBounds();
      pins.forEach((pin) => {
        bounds.extend(convertLongLatToLatLngLiteral(pin.bounds.top_left));
        bounds.extend(convertLongLatToLatLngLiteral(pin.bounds.bottom_right));
      });
      map.fitBounds(bounds);
      noNeedToFitBounds();
    }
  }, [map, performFittingBounds, pins, noNeedToFitBounds]);

  useEffect(() => {
    fitBounds();
  }, [fitBounds, pins]);

  useEffect(() => {
    needToFitBounds();
  }, [filter, needToFitBounds]);

  const { clusters, entities, multiPins } = useMemo(() => {
    return categorizeMapEntries(pins);
  }, [pins]);

  useMapEventHandler(
    map,
    "bounds_changed",
    useDebouncedCallback([
      function (this: google.maps.Map) {
        const bounds = this.getBounds();
        const zoom = this.getZoom();
        if (bounds && zoom) {
          onChangeBounds?.(convertLatLngBoundsToPlatformBounds(bounds), zoom);
        }
      },
      100,
      boundsChangeOptions,
    ])
  );

  return (
    <FeaturedMap
      className={styles.mapContainer}
      initialOptions={{
        center: { lat: 0, lng: 0 },
        clickableIcons: false,
        controlSize: 32,
        disableDefaultUI: true,
        gestureHandling: "cooperative",
        zoom: 16,
      }}
      mapClassName={styles.map}
      onInitialized={setMap}
    >
      {isActivity ? (
        <ActivityFeature
          entityPins={entities as unknown as ActivityPin[]}
          onClick={handlePinClick}
        />
      ) : (
        <EntityFeature
          entityPins={entities}
          onClick={handlePinClick}
          onDoubleClick={handlePinDoubleClick}
        />
      )}

      <MultiPinFeature
        entityType={getEntityTypeByMapFilter(filter) as EntityTypesSupportingMultiPin | undefined}
        multiPins={multiPins}
      />
      <ClusterFeature clusters={clusters} />
      {annotation && (
        <EntityAnnotation
          entityId={annotation.entityId}
          entityType={annotation.entityType}
          map={map}
          onHide={handleHideAnnotation}
        />
      )}
      {isActivity ? <MultiPinActivityListDrawer /> : <MultiPinRecordListDrawer />}
      <Interactive className={styles.zoomButtons}>
        <ZoomButtons />
      </Interactive>
    </FeaturedMap>
  );
};

const mapDispatchToProps = {
  onShowPreviewPane: showEntityView,
};

export default connect(null, mapDispatchToProps)(ActivityReportMap);
